import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { showErrorToast, showSuccessToast } from '@/hooks/use-toast';
import {
  useGetAgentById,
  useUpdateAgentAIConfigurations,
} from '@/modules/gpt-chats/hooks/use-agents';
import { useGetToolDetails } from '@/modules/gpt-chats/hooks/use-tools';
import { getToolIcon } from '@/modules/gpt-chats/utils/tool-utils';
import { BookOpen, Trash2 } from 'lucide-react';
import { MarkdownRenderer } from '@/modules/gpt-chats/components/markdown-renderer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui-kit/dialog';
import { Card, CardContent } from '@/components/ui-kit/card';
import { useEffect, useState } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';

export const AgentConfigToolCard = ({ toolId, agentId }: { toolId: string; agentId: string }) => {
  const { t } = useTranslation();
  const { data: toolData, isLoading } = useGetToolDetails(toolId, false);
  const { mutateAsync: updateAgentConfig } = useUpdateAgentAIConfigurations();
  const { data: agentData } = useGetAgentById(agentId);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [tutorialContent, setTutorialContent] = useState('');

  const defaultContent = `# ${t('TUTORIAL_GUIDE')}

  ${t('TUTORIAL_GUIDE_PLACEHOLDER')}`;

  useEffect(() => {
    setTutorialContent(toolData?.Tutorial ?? defaultContent);
  }, [toolData, defaultContent]);

  const handleRemoveTool = async () => {
    if (!agentData?.agent) return;

    try {
      const currentToolIds = agentData.agent.tool_ids || [];
      const newToolIds = currentToolIds.filter((id) => id !== toolId);

      const payload = {
        ...agentData.agent,
        agent_id: agentId,
        enable_tools: newToolIds.length > 0,
        tool_ids: newToolIds,
      };

      await updateAgentConfig(payload);
      showSuccessToast({ description: t('TOOL_REMOVED_SUCCESSFULLY') });
    } catch (error) {
      showErrorToast({
        title: t('FAILED_TO_REMOVE_TOOL'),
        errors: error,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-between rounded-lg border p-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10 rounded" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-48" />
          </div>
        </div>
        <Skeleton className="h-6 w-10" />
      </div>
    );
  }

  const tool = toolData;

  return (
    <div className="flex items-center justify-between rounded-lg border p-4">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded bg-primary">
          {getToolIcon(tool?.Type)}
        </div>
        <Link
          to={`/ai/tool-details/${toolId}/authentication?isBlocksTool=false`}
          className="group min-w-0 flex-1 cursor-pointer"
        >
          <div className="truncate font-medium text-high-emphasis transition-colors group-hover:text-primary">
            {tool?.Name || t('UNKNOWN_TOOL')}
          </div>
          <div className="line-clamp-1 text-sm text-medium-emphasis transition-colors group-hover:text-primary/80">
            {tool?.Description || t('NO_DESCRIPTION')}
          </div>
        </Link>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                className="text-medium-emphasis"
                variant="outline"
                onClick={() => setIsPreviewModalOpen(true)}
              >
                <BookOpen className="h-4 w-4" />
              </Button>
            </TooltipTrigger>

            <TooltipContent>
              <p>{t('TUTORIAL_GUIDE')}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Button
          variant="outline"
          onClick={handleRemoveTool}
          className="text-medium-emphasis hover:text-error"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <Dialog open={isPreviewModalOpen} onOpenChange={setIsPreviewModalOpen}>
        <DialogContent className="max-h-[600px] max-w-[600px] gap-8 overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('TUTORIAL_GUIDE_FOR', { name: tool?.Name ?? t('YOUR_TOOL') })}</DialogTitle>
          </DialogHeader>

          <Card className="min-h-[400px]">
            <CardContent>
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <MarkdownRenderer content={tutorialContent} />
              </div>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    </div>
  );
};
